import React from "react";
import { Container, Typography, Box } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PhoneIcon from "@mui/icons-material/Phone";
import { DP, PillButton, morphShape } from "./HomeStyles";
import { useReveal } from "../../hooks/useReveal";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.6) translateY(20px); }
  to   { opacity: 1; transform: scale(1)   translateY(0); }
`;
const orbitRotate = keyframes`
  from { transform: rotate(0deg)   translateX(70px) rotate(0deg); }
  to   { transform: rotate(360deg) translateX(70px) rotate(-360deg); }
`;
const glowRing = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(21,101,192,0.4), 0 0 40px rgba(21,101,192,0.2); }
  50%       { box-shadow: 0 0 0 20px rgba(21,101,192,0), 0 0 80px rgba(21,101,192,0.35); }
`;

const FinalCTA: React.FC = () => {
  const navigate = useNavigate();
  const { ref, visible } = useReveal(0.2);
  const { t, isRTL } = useLanguage();
  const tf = t.home.finalCta;

  return (
    <Box ref={ref} sx={{ py: { xs: 12, md: 18 }, background: `linear-gradient(160deg, ${DP.blueBg} 0%, #FFFFFF 40%, ${DP.blueBgDeep} 100%)`, position: "relative", overflow: "hidden", textAlign: "center" }}>
      {/* Background blobs */}
      {[
        { top: "-40%", right: "-8%", size: 500, dur: "20s" },
        { top: "60%",  left: "-10%", size: 400, dur: "25s" },
      ].map((b, i) => (
        <Box key={i} sx={{ position: "absolute", top: b.top, right: b.right, left: b.left, width: b.size, height: b.size, borderRadius: "50%", background: `radial-gradient(circle, ${DP.bluePale}50 0%, transparent 70%)`, animation: `${morphShape} ${b.dur} ease-in-out infinite ${i === 1 ? "reverse" : ""}`, pointerEvents: "none" }} />
      ))}

      {/* Floating paw prints */}
      {["🐾", "🐾", "🐾", "🐾"].map((p, i) => (
        <Box key={i} sx={{ position: "absolute", top: `${15 + i * 20}%`, left: i % 2 === 0 ? `${5 + i * 3}%` : "auto", right: i % 2 !== 0 ? `${5 + i * 2}%` : "auto", fontSize: "1.4rem", opacity: 0.06, animation: `${morphShape} ${12 + i * 3}s ease-in-out infinite`, animationDelay: `${i * 1.5}s`, pointerEvents: "none" }}>
          {p}
        </Box>
      ))}

      <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
        {/* Animated paw icon */}
        <Box sx={{ position: "relative", display: "inline-block", mb: 4, opacity: visible ? 1 : 0, animation: visible ? `${popIn} 0.7s cubic-bezier(0.34,1.56,0.64,1) 0.1s both` : "none" }}>
          <Box sx={{ width: 88, height: 88, borderRadius: "26px", background: `linear-gradient(135deg, ${DP.blue}, ${DP.blueLight})`, display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", fontSize: "2.4rem", boxShadow: `0 16px 48px ${DP.shadow}`, animation: visible ? `${glowRing} 3s ease-in-out infinite 0.8s` : "none", position: "relative", zIndex: 1 }}>
            🐾
          </Box>
          <Box sx={{ position: "absolute", top: "50%", left: "50%", width: 12, height: 12, borderRadius: "50%", marginTop: -6, marginLeft: -6, backgroundColor: DP.blueLight, boxShadow: `0 0 10px ${DP.blueLight}`, animation: visible ? `${orbitRotate} 3s linear infinite 1s` : "none" }} />
        </Box>

        <Typography variant="h2" sx={{ fontSize: { xs: "2.2rem", sm: "2.8rem", md: "3.8rem" }, fontWeight: 800, color: DP.textDark, letterSpacing: "-0.02em", lineHeight: 1.15, mb: 2.5, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(28px)", transition: "all 0.8s cubic-bezier(0.22,1,0.36,1) 0.2s" }}>
          {tf.heading}{" "}
          <Box component="span" sx={{ background: `linear-gradient(135deg, ${DP.blue}, ${DP.blueLight})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {tf.headingHighlight}
          </Box>
        </Typography>

        <Typography sx={{ fontSize: { xs: "1.05rem", md: "1.15rem" }, color: DP.textMuted, lineHeight: 1.85, mb: 6, maxWidth: 520, mx: "auto", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s cubic-bezier(0.22,1,0.36,1) 0.35s" }}>
          {tf.subtitle}{" "}
          <Box component="span" sx={{ color: DP.blue, fontWeight: 700 }}>{tf.subtitleHighlight}</Box>
        </Typography>

        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(16px)", transition: "all 0.8s cubic-bezier(0.22,1,0.36,1) 0.5s" }}>
          <PillButton variant="contained" endIcon={<ArrowForwardIcon sx={{ transform: isRTL ? "scaleX(-1)" : "none" }} />} onClick={() => navigate("/login")}
            sx={{ backgroundColor: DP.blue, color: DP.white, px: 5, py: 1.8, fontSize: "1.05rem", boxShadow: `0 8px 32px ${DP.shadow}`, position: "relative", overflow: "hidden", "&::before": { content: '""', position: "absolute", top: 0, left: "-100%", width: "100%", height: "100%", background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)", transition: "left 0.5s ease" }, "&:hover::before": { left: "100%" }, "&:hover": { backgroundColor: DP.navy, transform: "translateY(-4px) scale(1.02)" }, transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)" }}>
            {tf.startLearning}
          </PillButton>
          <Box component="a" href="tel:04 541 965" sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, px: 4, py: 1.8, borderRadius: "50px", border: `2px solid ${DP.border}`, color: DP.textDark, textDecoration: "none", fontWeight: 700, fontSize: "1.05rem", transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)", "&:hover": { borderColor: DP.blue, color: DP.blue, backgroundColor: DP.blueBg, transform: "translateY(-4px)", boxShadow: `0 8px 24px ${DP.border}` } }}>
            <PhoneIcon sx={{ fontSize: "1.1rem" }} />
            04 541 965
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default FinalCTA;
